import { TFunction } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { DocumentType } from '~/types/document';
import { DiscountType, DocumentDataLine } from '~/types/document-builder';
import { fromEInvoiceDettaglioLineeToItemsPreview } from '~/utils/einvoice';
import { formatDateForDisplay } from '~/utils/formatDate';
import { formatPercentageTwoDigits } from '~/utils/formatNumber';
import { formatMoney, formatMoneyOptions, newMoney } from '~/utils/money';
import { parseValue } from '~/utils/numberInputs';
import styles from './ItemsTable.module.scss';

interface Props {
  currency: string;
  disableTabularNums?: boolean;
  items: ReturnType<typeof fromEInvoiceDettaglioLineeToItemsPreview>;
  showCode: boolean;
  showUnit: boolean;
  showPrice: boolean;
  showDiscount: boolean;
  type: DocumentType;
}

const formatCompetencyPeriod = (value: DocumentDataLine, t: TFunction<'documents', 'builder'>): string | null => {
  const dates = [
    value.competencyStartDate ? formatDateForDisplay(value.competencyStartDate) : null,
    value.competencyEndDate ? formatDateForDisplay(value.competencyEndDate) : null,
  ].filter((date): date is string => date !== null);

  if (dates.length === 0) {
    return null;
  }

  return `${t('labels.competencyPeriod')}: ${dates.join(' - ')}`;
};

const DefaultTable: React.FC<Omit<Props, 'type' | 'showPrice'>> = ({
  currency,
  disableTabularNums,
  showCode,
  showUnit,
  showDiscount,
  items,
}) => {
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });
  return (
    <table cellSpacing={0} className={styles.table}>
      <thead>
        <tr>
          <th align="left">{t('preview.product')}</th>
          {showCode && <th>{t('preview.code')}</th>}
          <th align="right">{t('preview.quantity')}</th>
          {showUnit && <th>{t('preview.unit')}</th>}
          <th align="right">{t('preview.unit_price')}</th>
          {showDiscount && <th align="right">{t('preview.discount')}</th>}
          <th align="right">{t('preview.amount_net')}</th>
          <th align="right">{t('preview.amount_gross')}</th>
          <th align="right">{t('preview.vat_percentage')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => {
          const managementData = item.value.managementData.map((m) =>
            [m.dataType, m.text, m.date !== null ? formatDateForDisplay(m.date) : null, m.number]
              .filter((m) => m !== null && m !== '')
              .join(' - '),
          );

          const competencyPeriod = formatCompetencyPeriod(item.value, t);
          const adminReference = item.value.riferimentoAmministrazione.trim();

          return (
            <Fragment key={`${item.value.description}-${index}`}>
              <tr key={`${index}-main`}>
                <td width="30%">
                  <Text variant="caption" weight="medium">
                    {item.value.description}
                  </Text>
                </td>
                {showCode && (
                  <td>
                    <Text variant="caption" weight="medium">
                      {item.value.code || '-'}
                    </Text>
                  </td>
                )}
                <td align="right" width="5%">
                  <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                    {item.value.quantity}
                  </Text>
                </td>
                {showUnit && (
                  <td>
                    <Text variant="caption" weight="medium">
                      {item.value.unit || '-'}
                    </Text>
                  </td>
                )}
                <td align="right">
                  {item.value.netPrice && (
                    <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                      {formatMoneyOptions(newMoney(item.value.netPrice, currency), { maximumFractionDigits: 8 })}
                    </Text>
                  )}
                </td>
                {showDiscount && (
                  <td align="right" width="5%">
                    {item.value.discounts.map((discount, index) => (
                      <div key={index}>
                        <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                          {discount.value !== '0'
                            ? discount.type === DiscountType.Percentage
                              ? formatPercentageTwoDigits(parseValue(discount.value))
                              : formatMoney({ amount: discount.value, currency })
                            : '—'}
                        </Text>
                      </div>
                    ))}
                  </td>
                )}
                <td align="right">
                  <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                    {formatMoney(item.netTotal)}
                  </Text>
                </td>
                <td align="right">
                  <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                    {formatMoney(item.grossTotal)}
                  </Text>
                </td>
                <td align="right" width="5">
                  <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                    {formatPercentageTwoDigits(parseValue(item.value.vat.percentage))}
                  </Text>
                </td>
              </tr>

              <tr key={`${index}-details`}>
                <td width="30%">
                  <VStack gap={3}>
                    {item.value.extendedDescription !== '' && (
                      <Text variant="body2" weight="regular" secondaryColor className={styles.extendedDescription}>
                        {item.value.extendedDescription}
                      </Text>
                    )}
                    {!isEmpty(item.value.articleCodes) && (
                      <VStack gap={1}>
                        {item.value.articleCodes?.map((articleCode, index) => (
                          <Text variant="body2" weight="regular" key={index}>
                            {articleCode.type} - {articleCode.value}
                          </Text>
                        ))}
                      </VStack>
                    )}
                    {managementData.length > 0 && (
                      <VStack gap={1}>
                        {managementData.map((m, i) => (
                          <Text variant="caption" weight="medium" key={i} style={{ fontStyle: 'italic' }}>
                            {m}
                          </Text>
                        ))}
                      </VStack>
                    )}
                    {competencyPeriod && (
                      <Text variant="caption" weight="medium">
                        {competencyPeriod}
                      </Text>
                    )}
                    {adminReference && (
                      <Text variant="caption" weight="medium">
                        {`${t('labels.riferimento_amministrazione')}: ${adminReference}`}
                      </Text>
                    )}
                  </VStack>
                </td>
              </tr>
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const DeliveryNoteTable: React.FC<
  Pick<Props, 'currency' | 'disableTabularNums' | 'showCode' | 'showUnit' | 'showPrice' | 'showDiscount' | 'items'>
> = ({ currency, disableTabularNums, showCode, showUnit, showPrice, showDiscount, items }) => {
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });

  return (
    <table cellSpacing={0}>
      <thead>
        <tr>
          {showCode && <th>{t('preview.code')}</th>}
          <th align="left">{t('preview.product')}</th>
          <th align="right">{t('preview.quantity')}</th>
          {showUnit && <th>{t('preview.unit')}</th>}
          {showPrice && <th align="right">{t('preview.unit_price')}</th>}
          {showDiscount && <th align="right">{t('preview.discount')}</th>}
          {showPrice && (
            <>
              <th align="right">{t('preview.amount_net')}</th>
              <th align="right">{t('preview.amount_gross')}</th>
              <th align="right">{t('preview.vat_percentage')}</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => {
          const managementData = item.value.managementData.map((m) =>
            [m.dataType, m.text, m.date !== null ? formatDateForDisplay(m.date) : null, m.number]
              .filter((m) => m !== null && m !== '')
              .join(' - '),
          );
          const competencyPeriod = formatCompetencyPeriod(item.value, t);
          const adminReference = item.value.riferimentoAmministrazione.trim();
          return (
            <Fragment key={`${item.value.description}-${index}`}>
              <tr key={`${index}-main`}>
                {showCode && (
                  <td width="5%">
                    <Text variant="caption" weight="medium">
                      {item.value.code || '-'}
                    </Text>
                  </td>
                )}
                <td>
                  <Text variant="caption" weight="medium">
                    {item.value.description}
                  </Text>
                </td>
                <td align="right">
                  <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                    {item.value.quantity}
                  </Text>
                </td>
                {showUnit && (
                  <td>
                    <Text variant="caption" weight="medium">
                      {item.value.unit || '-'}
                    </Text>
                  </td>
                )}
                {showPrice && (
                  <td align="right">
                    {item.value.netPrice && (
                      <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                        {formatMoneyOptions(newMoney(item.value.netPrice, currency), { maximumFractionDigits: 8 })}
                      </Text>
                    )}
                  </td>
                )}
                {showDiscount && (
                  <td align="right" width="5%">
                    {item.value.discounts.map((discount, index) => (
                      <div key={index}>
                        <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                          {discount.value !== '0'
                            ? discount.type === DiscountType.Percentage
                              ? formatPercentageTwoDigits(parseValue(discount.value))
                              : formatMoney({ amount: discount.value, currency })
                            : '—'}
                        </Text>
                      </div>
                    ))}
                  </td>
                )}
                {showPrice && (
                  <>
                    <td align="right">
                      <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                        {formatMoney(item.netTotal)}
                      </Text>
                    </td>
                    <td align="right">
                      <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                        {formatMoney(item.grossTotal)}
                      </Text>
                    </td>
                    <td align="right" width="5">
                      <Text variant="caption" weight="medium" tabularNums={!disableTabularNums}>
                        {formatPercentageTwoDigits(parseValue(item.value.vat.percentage))}
                      </Text>
                    </td>
                  </>
                )}
              </tr>

              <tr key={`${index}-details`}>
                <td width="30%">
                  {item.value.extendedDescription !== '' && (
                    <Text variant="caption" weight="medium" secondaryColor className={styles.extendedDescription}>
                      {item.value.extendedDescription}
                    </Text>
                  )}
                  {managementData.length > 0 && (
                    <VStack gap={1}>
                      {managementData.map((m, i) => (
                        <Text variant="caption" weight="medium" key={i} style={{ fontStyle: 'italic' }}>
                          {m}
                        </Text>
                      ))}
                    </VStack>
                  )}
                  {competencyPeriod && (
                    <Text variant="caption" weight="medium">
                      {competencyPeriod}
                    </Text>
                  )}
                  {adminReference && (
                    <Text variant="caption" weight="medium">
                      {`${t('labels.riferimento_amministrazione')}: ${adminReference}`}
                    </Text>
                  )}
                </td>
              </tr>
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};
export const ItemsTable: React.FC<Props> = ({
  currency,
  disableTabularNums,
  showCode,
  showUnit,
  showPrice,
  showDiscount,
  items,
  type,
}) => {
  switch (type) {
    case DocumentType.DeliveryNote:
      return (
        <DeliveryNoteTable
          currency={currency}
          disableTabularNums={disableTabularNums}
          items={items}
          showCode={showCode}
          showUnit={showUnit}
          showPrice={showPrice}
          showDiscount={showDiscount}
        />
      );
    default:
      return (
        <DefaultTable
          currency={currency}
          disableTabularNums={disableTabularNums}
          items={items}
          showCode={showCode}
          showUnit={showUnit}
          showDiscount={showDiscount}
        />
      );
  }
};
